import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Paper,
  Chip,
  Divider,
  Button,
  CircularProgress,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { printReceipt as sendPrintReceipt } from '../../../../services/localPrintService';
import { getirApi } from '../../../../config/apiClient';
import { buildGetirReceiptItems } from '../utils/getirReceiptBuilder';
import { resolveReceiptHeader, resolveReceiptLabel, buildReceiptOrderRef, toDisplaySettings } from '../utils/receiptHeader';
import type { PlatformIntegration } from '../../../../types/platformIntegration';

// Backend'ten gelen GetirOrder entity formatı
interface GetirOrderDetail {
  id: number;
  getirId: string;
  confirmationId?: string;
  status?: string;
  statusText?: string;
  isScheduled: boolean;
  isQueued: boolean;
  scheduledDate?: string;
  checkoutDate?: string;
  verifyDate?: string;
  prepareDate?: string;
  handoverDate?: string;
  reachDate?: string;
  deliverDate?: string;
  clientId?: string;
  clientName?: string;
  clientPhone?: string;
  contactPhone?: string;
  deliveryAddressId?: string;
  deliveryAddress?: string;
  aptNo?: string;
  floor?: string;
  doorNo?: string;
  city?: string;
  district?: string;
  addressDescription?: string;
  clientNote?: string;
  doNotKnock: boolean;
  dropOffAtDoor: boolean;
  isEcoFriendly: boolean;
  clientLatitude?: number;
  clientLongitude?: number;
  courierId?: string;
  courierName?: string;
  courierStatus?: number;
  courierLatitude?: number;
  courierLongitude?: number;
  calculatedCourierToRestaurantETA?: number;
  paymentMethod: number;
  paymentMethodText?: string;
  totalPrice: number;
  totalDiscountedPrice?: number;
  deliveryType: number;
  restaurantId?: string;
  restaurantName?: string;
  brandId?: string;
  cancelReasonId?: string;
  cancelReasonText?: string;
  cancelledAt?: string;
  companyId?: string;
  branchId?: string;
  createdAt: string;
  updatedAt: string;
  rawJson?: string;
  notes?: string;
  items?: GetirOrderItemDetail[];
}

interface GetirOrderItemDetail {
  id: number;
  name: string;
  nameEn?: string;
  quantity: number;
  price: number;
  optionPrice: number;
  priceWithOption: number;
  totalPrice: number;
  totalOptionPrice: number;
  totalPriceWithOption: number;
  imageUrl?: string;
  wideImageUrl?: string;
  note?: string;
  options?: { id: number; optionName: string; price: number }[];
}

export interface GetirOrderDetailModalProps {
  open: boolean;
  onClose: () => void;
  orderId: number | null;
  companyId: string | null;
  /**
   * Online ödenmiş siparişler için Pavo cihazından mali fiş kestirme handler'i.
   * Tanimliysa modaldaki "Online Fis Yazdir" butonu aktif olur (sadece online
   * odenmis siparislerde gorunur).
   */
  onRequestOnlineReceipt?: () => void;
  screenSettings?: {
    printingSettings?: {
      defaultReceiptPrinter?: { id: string; name: string };
      adisyonPrinter?: { id: string; name: string } | null;
      receiptDisplaySettings?: {
        companyName?: string;
        companyAddress?: string;
        taxNumber?: string;
        headerType?: 'text' | 'logo';
        logoUrl?: string;
        logoMaxWidthPx?: number;
        logoMaxHeightPx?: number;
        standardFooterNote?: string;
        thankYouMessage?: string;
      };
    };
    localServiceUrl?: string;
    /** Marka bazlı fiş başlığını çözebilmek için entegrasyon listesi. */
    platformIntegrations?: PlatformIntegration[];
  };
}

// Status koduna göre bilgi
const getStatusLabel = (status: string | undefined): { label: string; color: 'default' | 'warning' | 'info' | 'success' | 'error' } => {
  const s = parseInt(status || '0');
  if (s === 400) return { label: 'Onay Bekleniyor', color: 'warning' };
  if (s === 500) return { label: 'Hazırlanıyor', color: 'info' };
  if (s === 550) return { label: 'Hazırlandı', color: 'success' };
  if (s === 600) return { label: 'Kuryeye Teslim Edildi', color: 'info' };
  if (s === 700) return { label: 'Kurye Yolda', color: 'info' };
  if (s === 800) return { label: 'Kurye Adrese Ulaştı', color: 'info' };
  if (s === 900) return { label: 'Teslim Edildi', color: 'success' };
  if (s === 1500) return { label: 'Admin İptal', color: 'error' };
  if (s === 1600) return { label: 'Restoran İptal', color: 'error' };
  return { label: status || 'Bilinmiyor', color: 'default' };
};

// Tarih formatlama
const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    return d.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  } catch {
    return dateStr;
  }
};

const formatTime = (dateStr?: string): string => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '-';
  }
};

const GetirOrderDetailModal: React.FC<GetirOrderDetailModalProps> = ({
  open,
  onClose,
  orderId,
  companyId,
  screenSettings,
  onRequestOnlineReceipt,
}) => {
  const [order, setOrder] = useState<GetirOrderDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);

  // Modal açıldığında sipariş detayını yükle
  useEffect(() => {
    if (open && orderId && companyId) {
      const fetchOrderDetail = async () => {
        setLoading(true);
        setError(null);
        try {
          const response = await getirApi.get(`/api/getir-orders/order/${orderId}`);
          const result = response.data;
          if (result.success && result.data) {
            setOrder(result.data);
          } else {
            setError(result.message || 'Sipariş detayı yüklenemedi');
          }
        } catch (err) {
          setError('Sipariş detayı yüklenirken hata oluştu');
          console.error('❌ [GetirDetail] Yükleme hatası:', err);
        } finally {
          setLoading(false);
        }
      };
      fetchOrderDetail();
    } else {
      setOrder(null);
    }
  }, [open, orderId, companyId]);

  // Getir fişi HTML şablonu
  const createGetirReceiptHtml = useCallback((o: GetirOrderDetail): string => {
    const receiptSettings = toDisplaySettings(
      resolveReceiptHeader(buildReceiptOrderRef('getir', null, o), screenSettings),
    );
    const items = o.items || [];
    const checkoutDate = o.checkoutDate ? new Date(o.checkoutDate) : new Date();
    const dateStr = checkoutDate.toLocaleDateString('tr-TR');
    const timeStr = checkoutDate.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    const hasValidDiscount = o.totalDiscountedPrice != null && o.totalDiscountedPrice > 0 && o.totalDiscountedPrice !== o.totalPrice;
    const finalTotal = hasValidDiscount ? o.totalDiscountedPrice! : o.totalPrice;
    const hasDiscountReceipt = hasValidDiscount;
    const deliveryLabel = o.deliveryType === 1 ? 'Getir Getirsin' : 'Restoran Getirsin';
    const paymentLabel = o.paymentMethodText || (o.paymentMethod === 1 ? 'Online Ödeme' : o.paymentMethod === 3 ? 'Kapıda Kart' : 'Kapıda Nakit');

    const notesParts = [
      o.clientNote,
      o.doNotKnock ? 'Kapıya vurma' : null,
      o.dropOffAtDoor ? 'Kapıya bırak' : null,
    ].filter(Boolean);

    return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Getir Sipariş Fişi</title>
    <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap" rel="stylesheet">
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: 'Poppins', sans-serif; font-size: 9pt; width: 72mm; max-width: 72mm; margin: 0; padding: 1mm 2mm 2mm 2mm; line-height: 1.2; color: #000; }
      .header { text-align: center; margin-bottom: 3px; }
      .header h2 { margin: 0 0 2px 0; }
      .header img { max-width: 50mm; max-height: 18mm; image-rendering: -webkit-optimize-contrast; }
      .getir-badge { text-align: center; margin: 5px 0; padding: 4px 0; font-weight: bold; font-size: 12pt; letter-spacing: 3px; border-top: 2px solid #000; border-bottom: 2px solid #000; }
      .order-number { font-size: 14pt; text-align: center; margin: 5px 0 8px 0; font-weight: bold; letter-spacing: 2px; }
      .customer-info { font-size: 10pt; margin: 5px 0; padding: 4px; border: 1px solid #000; }
      .customer-info p { margin: 2px 0; }
      .customer-name { font-weight: bold; font-size: 11pt; }
      .customer-address { font-size: 9pt; margin: 5px 0; padding: 4px; border: 1px solid #000; }
      .customer-address p { margin: 0; word-wrap: break-word; }
      .invoice-details { margin-bottom: 3px; font-size: 8pt; }
      .invoice-details p { margin: 0 0 1px 0; }
      .items-table { width: 100%; border-collapse: collapse; font-size: 8pt; margin-top: 2px; table-layout: fixed; }
      .items-table th, .items-table td { text-align: left; padding: 1px 0; }
      .items-table th { border-bottom: 1px solid #000; }
      .option-row td { font-size: 7pt; padding-left: 8px; }
      .summary { margin-top: 5px; text-align: right; font-size: 9pt; }
      .summary p { margin: 0 0 2px 0; }
      .discount-line { font-weight: bold; }
      .grand-total { font-size: 13pt; font-weight: bold; text-align: center; margin: 8px 0; padding: 5px 0; }
      .order-note { font-size: 8pt; margin: 5px 0; padding: 3px; border: 1px dashed #000; }
      .delivery-type { text-align: center; font-size: 9pt; font-weight: bold; padding: 3px 0; margin: 4px 0; border-bottom: 1px dashed #000; }
      .payment-info { text-align: center; font-size: 10pt; font-weight: bold; padding: 4px 0; margin: 5px 0; border: 1px solid #000; }
      .receipt-label { font-size: 10pt; text-align: center; margin: 8px 0; font-weight: bold; border: 1px solid #000; padding: 3px; letter-spacing: 2px; }
      .footer { margin-top: 5px; text-align: center; font-size: 8pt; border-top: 1px dashed #000; padding-top: 3px; }
      .footer p { margin: 0 0 1px 0; }
      .right { text-align: right; }
      .bold { font-weight: bold; }
    </style></head><body>
      <div class="header">
        ${receiptSettings?.headerType === 'logo' && receiptSettings?.logoUrl
          ? `<img src="${receiptSettings.logoUrl}" alt="Logo" />`
          : `<h2>${receiptSettings?.companyName || 'AQUASOFT'}</h2>`}
      </div>
      ${receiptSettings?.headerType === 'logo' && receiptSettings?.logoUrl ? '' : `
        <div style="text-align:center;font-size:8pt;">
          ${receiptSettings?.companyAddress ? `<p style="margin:0 0 1px 0">${receiptSettings.companyAddress}</p>` : ''}
          ${receiptSettings?.taxNumber ? `<p style="margin:0 0 1px 0">VKN: ${receiptSettings.taxNumber}</p>` : ''}
        </div>
      `}
      <div class="getir-badge">* GETIR SIPARISI *</div>
      <div class="order-number">SIPARIS: ${o.confirmationId || o.getirId?.substring(0, 8) || o.id}</div>
      ${o.isScheduled ? `
      <div style="text-align:center;margin:5px 0;padding:5px;border:2px solid #000;font-weight:bold;">
        <span style="font-size:11pt;">⏰ İLERİ TARİHLİ SİPARİŞ</span>
        ${o.scheduledDate ? `<br/><span style="font-size:12pt;">TESLİM: ${(() => {
          const utcStr = o.scheduledDate!;
          const utcDate = utcStr.endsWith('Z') ? new Date(utcStr) : new Date(utcStr + 'Z');
          const trMs = utcDate.getTime() + 3 * 60 * 60 * 1000;
          const trDate = new Date(trMs);
          const h = String(trDate.getUTCHours()).padStart(2, '0');
          const m = String(trDate.getUTCMinutes()).padStart(2, '0');
          const d = String(trDate.getUTCDate()).padStart(2, '0');
          const mo = String(trDate.getUTCMonth() + 1).padStart(2, '0');
          return d + '.' + mo + ' ' + h + ':' + m;
        })()}</span>` : ''}
      </div>
      ` : ''}
      <div class="customer-info">
        <p style="font-weight:bold;font-size:8pt;margin:0 0 3px 0;border-bottom:1px solid #000;padding-bottom:2px;">MUSTERI BILGILERI</p>
        <p><span class="bold">Ad:</span> ${o.clientName || 'Müşteri'}</p>
        ${o.clientPhone ? `<p><span class="bold">Tel:</span> ${o.clientPhone}</p>` : ''}
      </div>
      <div class="customer-address">
        <p style="font-weight:bold;font-size:8pt;margin:0 0 3px 0;border-bottom:1px solid #000;padding-bottom:2px;">ADRES</p>
        <p>${(() => {
          if (o.deliveryAddress) return o.deliveryAddress;
          const parts: string[] = [];
          if (o.district) parts.push(o.district);
          if (o.city && o.city !== o.district) parts.push(o.city);
          if (o.aptNo) parts.push(`Bina: ${o.aptNo}`);
          if (o.floor) parts.push(`Kat: ${o.floor}`);
          if (o.doorNo) parts.push(`Daire: ${o.doorNo}`);
          return parts.join(', ') || '-';
        })()}</p>
        ${o.addressDescription ? `<p style="margin-top:4px;padding:3px;border:1px dashed #000;"><span style="font-weight:bold;">TARIF:</span> ${o.addressDescription}</p>` : ''}
      </div>
      <div class="invoice-details">
        <p><span class="bold">Tarih:</span> ${dateStr} ${timeStr}</p>
        <p><span class="bold">Odeme:</span> ${paymentLabel}</p>
      </div>
      <div class="delivery-type">${deliveryLabel}</div>
      ${notesParts.length > 0 ? `<div class="order-note">${notesParts.join(' | ')}</div>` : ''}
      <table class="items-table">
        <thead><tr>
          <th style="width:45%;">Urun</th>
          <th class="right" style="width:12%;">Adet</th>
          <th class="right" style="width:20%;">Fiyat</th>
          <th class="right" style="width:23%;">Tutar</th>
        </tr></thead>
        <tbody>
          ${items.map(item => {
            let rows = `<tr>
              <td>${item.name}</td>
              <td class="right">${item.quantity}</td>
              <td class="right">${item.price.toFixed(2)}</td>
              <td class="right">${(item.totalPriceWithOption || item.totalPrice).toFixed(2)}</td>
            </tr>`;
            if (item.options && item.options.length > 0) {
              rows += item.options.map(opt =>
                `<tr class="option-row"><td colspan="4">+ ${opt.optionName}${opt.price > 0 ? ` (+${opt.price.toFixed(2)})` : ''}</td></tr>`
              ).join('');
            }
            if (item.note) {
              rows += `<tr class="option-row"><td colspan="4">Not: ${item.note}</td></tr>`;
            }
            return rows;
          }).join('')}
        </tbody>
      </table>
      <div class="summary">
        <p>Ara Toplam: ${o.totalPrice.toFixed(2)} TL</p>
        ${hasDiscountReceipt ? `<p class="discount-line">Indirim: -${(o.totalPrice - o.totalDiscountedPrice!).toFixed(2)} TL</p>` : ''}
      </div>
      <div class="grand-total">TOPLAM: ${finalTotal.toFixed(2)} TL</div>
      ${o.paymentMethod === 1 ? '<div class="payment-info">** ONLINE ODENDI **</div>' : ''}
      <div class="receipt-label">GETIR PAKET SIPARIS FISI</div>
      <div class="footer">
        ${receiptSettings?.standardFooterNote ? `<p>${receiptSettings.standardFooterNote}</p>` : ''}
        ${receiptSettings?.thankYouMessage ? `<p>${receiptSettings.thankYouMessage}</p>` : ''}
      </div>
    </body></html>`;
  }, [screenSettings]);

  // Fiş yazdırma
  const handlePrintReceipt = useCallback(async () => {
    if (!order) return;

    const printer = screenSettings?.printingSettings?.defaultReceiptPrinter
      || screenSettings?.printingSettings?.adisyonPrinter;
    if (!printer) {
      alert('Fiş yazıcısı tanımlanmamış. Lütfen ekran ayarlarından yazıcı seçin.');
      return;
    }

    setIsPrinting(true);
    try {
      const localServiceUrl = screenSettings?.localServiceUrl || 'http://localhost:3003';
      // Markaya özel fiş başlığı; marka tanımlı değilse ekran varsayılanı.
      const brandHeader = resolveReceiptHeader(buildReceiptOrderRef('getir', null, order), screenSettings);
      const checkoutDate = order.checkoutDate ? new Date(order.checkoutDate) : new Date();
      const deliveryLabel = order.deliveryType === 1 ? 'Getir Getirsin' : 'Restoran Getirsin';
      const notesParts = [
        order.clientNote,
        order.doNotKnock ? 'Kapıya vurma' : null,
        order.dropOffAtDoor ? 'Kapıya bırak' : null,
      ].filter(Boolean);
      let addrStr = '';
      if (order.deliveryAddress) {
        addrStr = order.deliveryAddress;
      } else {
        addrStr = [order.district, order.city !== order.district ? order.city : null].filter(Boolean).join(', ');
      }
      const receiptData = {
        // `useKitchenPrintHandlers` (otomatik yazdırma) ile **aynı** helper'ı
        // kullanıyoruz; opsiyon/not formatlamasında ufak bir tutarsızlık bile
        // adisyonda farklı çıktıya yol açıyordu.
        items: buildGetirReceiptItems(order),
        orderNumber: order.confirmationId || order.getirId?.substring(0, 8) || String(order.id),
        customerName: order.clientName || 'Müşteri',
        customerPhone: order.clientPhone || '',
        deliveryAddress: addrStr || undefined,
        customerAddress: addrStr || undefined,
        binaNo: order.aptNo || undefined,
        kat: order.floor || undefined,
        daireNo: order.doorNo || undefined,
        addressDescription: order.addressDescription || undefined,
        invoiceNumber: order.confirmationId || String(order.id),
        date: checkoutDate.toLocaleDateString('tr-TR'),
        time: checkoutDate.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }),
        grandTotal: (order.totalDiscountedPrice != null && order.totalDiscountedPrice > 0 && order.totalDiscountedPrice !== order.totalPrice)
          ? order.totalDiscountedPrice : order.totalPrice,
        discountTotal: (order.totalDiscountedPrice != null && order.totalDiscountedPrice > 0 && order.totalDiscountedPrice !== order.totalPrice)
          ? order.totalPrice - order.totalDiscountedPrice : 0,
        subtotal: order.totalPrice,
        paymentMethod: order.paymentMethodText
          || (order.paymentMethod === 1 ? 'Online Ödeme'
            : order.paymentMethod === 3 ? 'Kapıda Kart'
            : 'Kapıda Nakit'),
        isOnlinePaid: order.paymentMethod === 1,
        receiptLabel: resolveReceiptLabel(
          'GETIR PAKET SIPARIS',
          buildReceiptOrderRef('getir', null, order),
          screenSettings,
        ),
        deliveryType: deliveryLabel,
        // Template (PackageReceiptPrintService.tsx:387) sadece `data.orderNote`
        // render eder; `data.customerNote` HIC render edilmez. Eski kod
        // `customerNote` alanina yaziyordu -> manuel akistan basilan fiste
        // notlar/teslim talimatlari (Kapiya vurma / birak) gorunmuyordu.
        // Auto akis ile ayni alana (orderNote) yaziyoruz.
        orderNote: notesParts.length > 0 ? notesParts.join(' | ') : undefined,
        ...brandHeader,
        logoMaxWidthPx: brandHeader.logoMaxWidthPx ?? 384,
        logoMaxHeightPx: brandHeader.logoMaxHeightPx ?? 150,
        showReceiptHeader: true,
        isPreOrder: order.isScheduled || false,
        expectedDeliveryTime: order.scheduledDate || undefined,
      };

      await sendPrintReceipt({
        printerId: printer.id,
        printerName: printer.name,
        receiptData
      }, { baseUrl: localServiceUrl });
      console.log('✅ [GetirDetail] Fiş yazdırıldı');
    } catch (err: any) {
      console.error('❌ [GetirDetail] Fiş yazdırma hatası:', err);
      alert(`Fiş yazdırma hatası: ${err.message || 'Bilinmeyen hata'}`);
    } finally {
      setIsPrinting(false);
    }
  }, [order, screenSettings]);

  const statusInfo = getStatusLabel(order?.status);
  const isDeliveryType1 = order?.deliveryType === 1;
  const hasDiscount = order && order.totalDiscountedPrice != null && order.totalDiscountedPrice > 0 && order.totalDiscountedPrice !== order.totalPrice;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}
    >
      {/* Başlık */}
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: '#5D3EBC',
        color: 'white',
        py: 1.5,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ShoppingCartIcon sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Getir Sipariş Detayı
            </Typography>
            {order && (
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                #{order.confirmationId || order.getirId?.substring(0, 8)}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* Loading */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: '#5D3EBC' }} />
          </Box>
        )}

        {/* Error */}
        {error && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="error" variant="h6">{error}</Typography>
          </Box>
        )}

        {/* Sipariş Detayı */}
        {order && !loading && (
          <>
            {/* İleri Tarihli Sipariş Uyarısı */}
            {order.isScheduled && (
              <Box sx={{
                bgcolor: '#ff6f00',
                color: 'white',
                p: 2,
                mb: 2,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                border: '2px solid #e65100',
                animation: 'getirPreorderBlink 1.5s ease-in-out infinite',
                '@keyframes getirPreorderBlink': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.7 },
                },
              }}>
                <Box sx={{ fontSize: 40 }}>⏰</Box>
                <Box>
                  <Typography variant="h6" fontWeight="bold">İLERİ TARİHLİ SİPARİŞ</Typography>
                  {order.scheduledDate && (
                    <Typography variant="h5" fontWeight="bold">
                      Teslim: {(() => {
                        const utcStr = order.scheduledDate!;
                        const utcDate = utcStr.endsWith('Z') ? new Date(utcStr) : new Date(utcStr + 'Z');
                        const trMs = utcDate.getTime() + 3 * 60 * 60 * 1000;
                        const trDate = new Date(trMs);
                        const h = String(trDate.getUTCHours()).padStart(2, '0');
                        const m = String(trDate.getUTCMinutes()).padStart(2, '0');
                        const d = String(trDate.getUTCDate()).padStart(2, '0');
                        const mo = String(trDate.getUTCMonth() + 1).padStart(2, '0');
                        return `${d}.${mo} ${h}:${m}`;
                      })()}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            {/* Üst Bilgi Kartları */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              {/* Durum */}
              <Paper elevation={2} sx={{ p: 2, flex: 1, minWidth: 160 }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">DURUM</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={`${statusInfo.label} (${order.status})`}
                    color={statusInfo.color}
                    size="small"
                  />
                </Box>
              </Paper>

              {/* Sipariş Zamanı */}
              <Paper elevation={2} sx={{ p: 2, flex: 1, minWidth: 160 }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">SİPARİŞ ZAMANI</Typography>
                <Typography variant="h6" fontWeight="bold">{formatTime(order.checkoutDate)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {order.checkoutDate ? new Date(order.checkoutDate).toLocaleDateString('tr-TR') : '-'}
                </Typography>
              </Paper>

              {/* Teslimat Tipi */}
              <Paper elevation={2} sx={{ p: 2, flex: 1, minWidth: 160, bgcolor: isDeliveryType1 ? '#E3F2FD' : '#FFF3E0' }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">TESLİMAT TİPİ</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  {isDeliveryType1 ? <LocalShippingIcon color="info" /> : <StorefrontIcon color="warning" />}
                  <Typography variant="body1" fontWeight="bold">
                    {isDeliveryType1 ? 'Getir Getirsin' : 'Restoran Getirsin'}
                  </Typography>
                </Box>
              </Paper>

              {/* Ödeme */}
              <Paper elevation={2} sx={{ p: 2, flex: 1, minWidth: 160 }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">ÖDEME</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {order.paymentMethodText || (order.paymentMethod === 1 ? 'Online Ödeme' : order.paymentMethod === 3 ? 'Kapıda Kart' : 'Kapıda Nakit')}
                </Typography>
              </Paper>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Müşteri Bilgileri */}
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              👤 Müşteri Bilgileri
            </Typography>
            <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Ad Soyad</Typography>
                  <Typography variant="body1" fontWeight="bold">{order.clientName || '-'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Telefon</Typography>
                  <Typography variant="body1">{order.clientPhone || '-'}</Typography>
                  {order.contactPhone && order.contactPhone !== order.clientPhone && (
                    <Typography variant="caption" color="text.secondary">İletişim: {order.contactPhone}</Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Adres</Typography>
                  <Typography variant="body1">{order.deliveryAddress || '-'}</Typography>
                  <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                    {order.aptNo && (
                      <Typography variant="caption" color="text.secondary">Bina No: <strong>{order.aptNo}</strong></Typography>
                    )}
                    {order.floor && (
                      <Typography variant="caption" color="text.secondary">Kat: <strong>{order.floor}</strong></Typography>
                    )}
                    {order.doorNo && (
                      <Typography variant="caption" color="text.secondary">Daire: <strong>{order.doorNo}</strong></Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">İlçe</Typography>
                  <Typography variant="body2">{order.district || '-'}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">İl</Typography>
                  <Typography variant="body2">{order.city || '-'}</Typography>
                </Grid>
                {order.addressDescription && (
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, bgcolor: '#FFF8E1', borderRadius: 1, border: '1px dashed #F9A825' }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="bold">Adres Tarifi</Typography>
                      <Typography variant="body1" fontWeight="bold">{order.addressDescription}</Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>

              {/* Özel Talepler */}
              {(order.doNotKnock || order.dropOffAtDoor || order.isEcoFriendly || order.clientNote) && (
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {order.doNotKnock && <Chip label="🚪 Kapıya Vurma" size="small" color="warning" variant="outlined" />}
                    {order.dropOffAtDoor && <Chip label="📦 Kapıya Bırak" size="small" color="info" variant="outlined" />}
                    {order.isEcoFriendly && <Chip label="🌱 Çevre Dostu" size="small" color="success" variant="outlined" />}
                  </Box>
                  {order.clientNote && (
                    <Box sx={{ mt: 1, p: 1.5, bgcolor: 'warning.light', borderRadius: 1 }}>
                      <Typography variant="body2" color="warning.dark" fontWeight="medium">
                        📝 Müşteri Notu: {order.clientNote}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Paper>

            <Divider sx={{ my: 2 }} />

            {/* Sipariş Kalemleri */}
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              🛒 Sipariş Kalemleri ({order.items?.length || 0} ürün)
            </Typography>

            <Box sx={{ maxHeight: 300, overflowY: 'auto', mb: 2 }}>
              {order.items?.map((item, index) => (
                <Paper
                  key={item.id || index}
                  elevation={1}
                  sx={{ p: 2, mb: 1.5, display: 'flex', gap: 2, alignItems: 'flex-start' }}
                >
                  {/* Ürün Görseli */}
                  {item.imageUrl && (
                    <Box
                      component="img"
                      src={item.imageUrl}
                      alt={item.name}
                      sx={{
                        width: 60, height: 60,
                        objectFit: 'cover',
                        borderRadius: 1,
                        flexShrink: 0,
                      }}
                      onError={(e: any) => { e.target.style.display = 'none'; }}
                    />
                  )}

                  {/* Ürün Bilgileri */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" fontWeight="bold">{item.name}</Typography>
                    {item.nameEn && item.nameEn !== item.name && (
                      <Typography variant="caption" color="text.secondary">{item.nameEn}</Typography>
                    )}
                    {/* Opsiyonlar */}
                    {item.options && item.options.length > 0 && (
                      <Box sx={{ mt: 0.5 }}>
                        {item.options.map((opt, oi) => (
                          <Typography key={oi} variant="caption" color="text.secondary" component="div">
                            ➕ {opt.optionName}{opt.price > 0 ? ` (+${opt.price.toFixed(2)} ₺)` : ''}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {item.note && (
                      <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
                        📝 {item.note}
                      </Typography>
                    )}
                  </Box>

                  {/* Miktar ve Fiyat */}
                  <Box sx={{ textAlign: 'right', minWidth: 100 }}>
                    <Typography variant="body2" color="text.secondary">
                      {item.quantity} x {item.price.toFixed(2)} ₺
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary.main">
                      {item.totalPrice.toFixed(2)} ₺
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Tutar Özeti */}
            <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">Ürünler Toplamı</Typography>
                <Typography variant="body1" fontWeight="bold">{order.totalPrice.toFixed(2)} ₺</Typography>
              </Box>
              {hasDiscount && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1" color="error.main">İndirim</Typography>
                    <Typography variant="body1" color="error.main" fontWeight="bold">
                      -{(order.totalPrice - order.totalDiscountedPrice!).toFixed(2)} ₺
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6" fontWeight="bold">TOPLAM</Typography>
                    <Typography variant="h5" fontWeight="bold" color="success.main">
                      {order.totalDiscountedPrice!.toFixed(2)} ₺
                    </Typography>
                  </Box>
                </>
              )}
              {!hasDiscount && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '2px solid', borderColor: 'divider' }}>
                  <Typography variant="h6" fontWeight="bold">TOPLAM</Typography>
                  <Typography variant="h5" fontWeight="bold" color="success.main">
                    {order.totalPrice.toFixed(2)} ₺
                  </Typography>
                </Box>
              )}
            </Paper>

            {/* Kurye Bilgileri */}
            {order.courierName && (
              <>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  🛵 Kurye Bilgileri
                </Typography>
                <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Kurye</Typography>
                      <Typography variant="body1" fontWeight="bold">{order.courierName}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Kurye Durumu</Typography>
                      <Typography variant="body1">{order.courierStatus || '-'}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </>
            )}

            {/* Zaman Çizelgesi */}
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              ⏱️ Zaman Çizelgesi
            </Typography>
            <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
              <Grid container spacing={1}>
                {[
                  { label: 'Sipariş Oluşturma', date: order.checkoutDate },
                  { label: 'Onay (Verify)', date: order.verifyDate },
                  { label: 'Hazırlandı (Prepare)', date: order.prepareDate },
                  { label: 'Kuryeye Teslim (Handover)', date: order.handoverDate },
                  { label: 'Kurye Ulaştı (Reach)', date: order.reachDate },
                  { label: 'Teslim Edildi (Deliver)', date: order.deliverDate },
                  { label: 'İptal', date: order.cancelledAt },
                ].filter(t => t.date).map((t, i) => (
                  <Grid item xs={12} sm={6} key={i}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">{t.label}</Typography>
                      <Typography variant="body2" fontWeight="bold">{formatDate(t.date)}</Typography>
                    </Box>
                  </Grid>
                ))}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">Son Güncelleme</Typography>
                    <Typography variant="body2" fontWeight="bold">{formatDate(order.updatedAt)}</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* İptal Bilgileri */}
            {order.cancelReasonText && (
              <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: '#FFF3F3', border: '1px solid #f44336' }}>
                <Typography variant="subtitle2" color="error.main" fontWeight="bold" gutterBottom>
                  ❌ İptal Bilgisi
                </Typography>
                <Typography variant="body1">{order.cancelReasonText}</Typography>
                {order.cancelledAt && (
                  <Typography variant="caption" color="text.secondary">
                    İptal Zamanı: {formatDate(order.cancelledAt)}
                  </Typography>
                )}
              </Paper>
            )}

          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={isPrinting ? <CircularProgress size={18} color="inherit" /> : <PrintIcon />}
            onClick={handlePrintReceipt}
            disabled={!order || isPrinting}
            sx={{ bgcolor: '#5D3EBC', '&:hover': { bgcolor: '#4A2FA0' } }}
          >
            {isPrinting ? 'Yazdırılıyor...' : 'Fiş Yazdır'}
          </Button>
          {/* Online ödenmiş Getir siparisleri icin REST-P2P uzerinden Pavo
              cihazindan mali fis kestirme. Sadece paymentMethod === 1 (Online)
              olan siparislerde gorunur. */}
          {onRequestOnlineReceipt && order && order.paymentMethod === 1 && (
            <Button
              variant="contained"
              color="success"
              startIcon={<ReceiptLongIcon />}
              onClick={onRequestOnlineReceipt}
              sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
            >
              Online Fiş Yazdır
            </Button>
          )}
        </Box>
        <Button variant="outlined" onClick={onClose}>
          Kapat
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GetirOrderDetailModal;
